import { GridView, TableRows } from '@mui/icons-material'
import {
  Autocomplete,
  autocompleteClasses,
  Button,
  ButtonGroup,
  TextField,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import UserCard from 'common/components/UserCard'
import useCurrentMenu from 'common/hooks/useCurrentMenu'
import { CustomThemeContext } from 'common/providers/CustomThemeProvider'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import DocLaboralSkeleton from '../components/DocLaboralSkeleton'
import Laboral from '../components/LaboralGridView'
import LaboralTableView from '../components/LaboralTableView'
import useFetchLaboralMeta from '../hooks/useFetchLaboralMeta'

/**
 * @module Documentacion
 * @module DocumentacionLaboral
 */

/**
 * React Component that works as the default page for the Laboral Docs
 *
 * @param {Object} [props]
 * @param {string} [props.object] - This object comes from LIBRA `objeto-programa` *mmenu* and will be used to determine wich tipe of document will we fetched if it is not passed it will fetch all of the types
 * @example
 * <DocLaboral /> // This will fetch all the document types
 * <DocLaboral object="NOM" /> // This will fetch only the documents where rh_documento = "NOM"
 */
const DocLaboral = ({ object: tipo_documento }) => {
  const { themeController } = useContext(CustomThemeContext)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [user] = useState(location?.state?.user ?? null)
  const [userData, _] = useState({
    user: location?.state?.user ?? null,
    surname: location?.state?.surname ?? null,
    name: location?.state?.name ?? null,
    image: location?.state?.image ?? null,
  })

  const { actualMatch, currentMenu } = useCurrentMenu()
  const [currentIteration, setCurrentIteration] = useState(1)
  const [viewType, setViewType] = useState('TABLE')
  const [selectedDocType, setSelectedDocType] = useState(
    tipo_documento || searchParams.get('tipo_documento') || null,
  )
  const [availableDocTypes, setAvailableDocTypes] = useState([])
  const [ejercicioActivo, setEjercicioActivo] = useState(
    searchParams.get('ejercicio') || null,
  )
  const {
    response: { ejercicios: ejercicios_disponibles },
    loading,
  } = useFetchLaboralMeta(selectedDocType, currentIteration, user)
  const { response: { tipos_documentos: all_tipos_documentos } } = useFetchLaboralMeta(
    null,
    0,
    user,
  )

  useEffect(() => {
    if (!availableDocTypes || availableDocTypes.length === 0) {
      setAvailableDocTypes(all_tipos_documentos)
    }
  }, [all_tipos_documentos])

  useEffect(() => {
    if (!loading) {
      setSelectedDocType(tipo_documento || searchParams.get('tipo_documento') || null)
      setCurrentIteration(currentIteration + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualMatch?.camino, currentMenu, tipo_documento])

  useEffect(() => {
    if (!tipo_documento) {
      setSelectedDocType(searchParams.get('tipo_documento') || null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    if (!ejercicioActivo && ejercicios_disponibles && ejercicios_disponibles.length > 0)
      // @ts-ignore
      setEjercicioActivo(searchParams.get('ejercicio') || ejercicios_disponibles[0].ejercicio)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ejercicios_disponibles?.length, tipo_documento])

  return (
    <>
      {user ? <UserCard datos={userData} /> : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '10px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '5px' }}>
          {ejercicios_disponibles ? (
            ejercicios_disponibles.map((value, index) => (
              <Button
                key={index}
                color={ejercicioActivo === value.ejercicio ? 'success' : 'primary'}
                // @ts-ignore
                onClick={() => setEjercicioActivo(value.ejercicio)}
                variant={ejercicioActivo === value.ejercicio ? 'contained' : 'text'}
              >
                {value.ejercicio}
              </Button>
            ))
          ) : (
            <></>
          )}
        </Box>

        {!tipo_documento &&
        availableDocTypes &&
        !themeController.hide?.DocLaboral?.DocumentTypeFilter === true ? (
          <Autocomplete
            options={availableDocTypes ?? []}
            getOptionLabel={(option) => option.d_rh_documento}
            onChange={(event, value) => {
              setSelectedDocType(value?.nom_tipo)
            }}
            sx={{
              minWidth: '320px',
              ['& .MuiOutlinedInput-root']: {
                p: 2,
                width: '100%',
              },
              [`& .${autocompleteClasses.input}`]: {
                borderRadius: '5px',
                p: '0',
                '&::placeholder': {
                  opacity: 1,
                },
              },
              '& .MuiButtonBase-root': {},
              '& .MuiInputBase-root': {
                backgroundColor: 'background.paper',
                borderRadius: '1000px',
              },
              '& fieldset': { border: 'none', color: 'red', padding: '0px' },
              '&::placeholder': {
                color: 'white',
                fontSize: '1em',
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Tipo de documento"
                variant="outlined"
                inputProps={{ ...params.inputProps }}
              />
            )}
          />
        ) : (
          <></>
        )}

        <ButtonGroup>
          <Button
            variant={viewType === 'TABLE' ? 'contained' : 'text'}
            color={viewType === 'Table' ? 'primary' : 'success'}
            onClick={() => {
              setViewType('TABLE')
            }}
          >
            <TableRows />
          </Button>
          <Button
            variant={viewType === 'TABLE' ? 'text' : 'contained'}
            color={viewType === 'Table' ? 'primary' : 'success'}
            onClick={() => {
              setViewType('GRID')
            }}
          >
            <GridView />
          </Button>
        </ButtonGroup>
      </Box>
      {ejercicioActivo ? (
        viewType === 'TABLE' ? (
          <LaboralTableView ejercicio={ejercicioActivo} document_type={selectedDocType} />
        ) : (
          <Laboral ejercicio={ejercicioActivo} document_type={selectedDocType} />
        )
      ) : loading ? (
        <Box sx={{ mt: 1 }}>
          <DocLaboralSkeleton />
        </Box>
      ) : (
        <Box sx={{ width: '100%', py: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ opacity: '0.3' }}>
            No hay documentos disponibles
          </Typography>
        </Box>
      )}
    </>
  )
}

export default DocLaboral
